import { type Whoami } from '@/types/whoami';

import { domainMeta } from './domain-meta';
import { isSourceFreemium } from './is-source-freemium';

export type SourcePlanTier = 'paid' | 'freemium' | 'none';
export type SourceUserRole =
  | 'admin'
  | 'recruiter'
  | 'hiring_manager'
  | 'operator';
export type SourceEnvironment = 'production' | 'staging' | 'development';

const ENVIRONMENT_BY_DOMAIN_ENVIRONMENT: Record<
  typeof domainMeta.environment,
  SourceEnvironment
> = {
  prod: 'production',
  // uat is pre-production traffic, so it folds into staging and stays out of
  // production dashboards.
  stage: 'staging',
  uat: 'staging',
  dev: 'development',
};

// whoami.role is a free-text string from the monolith (only 'Admin' is
// checked elsewhere in this app, in auth-context.tsx), not a controlled
// enum. Normalize known values and default to 'recruiter', the role this
// app is built for.
const USER_ROLE_BY_WHOAMI_ROLE: Record<string, SourceUserRole> = {
  admin: 'admin',
  recruiter: 'recruiter',
  'hiring manager': 'hiring_manager',
  hiring_manager: 'hiring_manager',
  operator: 'operator',
};

export function mapUserRole(role: string | undefined): SourceUserRole {
  if (!role) return 'recruiter';
  return USER_ROLE_BY_WHOAMI_ROLE[role.toLowerCase()] ?? 'recruiter';
}

export function mapEnvironment(): SourceEnvironment {
  return ENVIRONMENT_BY_DOMAIN_ENVIRONMENT[domainMeta.environment];
}

/**
 * `source-freemium` gates restricted accounts; `sourcing-dashboard-user-access`
 * is the base Source entitlement paid accounts keep (see is-source-freemium.ts).
 * Neither flag set means Source isn't enabled for this account at all.
 */
export function mapPlanTier(whoami: Whoami | null | undefined): SourcePlanTier {
  if (isSourceFreemium(whoami)) return 'freemium';
  if (whoami?.feature_flags?.['sourcing-dashboard-user-access'] === true) {
    return 'paid';
  }
  return 'none';
}

export interface StandardEventProperties {
  product: 'source';
  ui: 'recruiter';
  environment: SourceEnvironment;
  account_id: string | undefined;
  user_id: string | undefined;
  user_role: SourceUserRole;
  source_plan_tier: SourcePlanTier;
}

/**
 * The standard properties every Source event carries.
 */
export function buildStandardEventProperties(
  whoami: Whoami | null | undefined,
): StandardEventProperties {
  return {
    product: 'source',
    ui: 'recruiter',
    environment: mapEnvironment(),
    account_id: whoami?.account_id,
    user_id: whoami?.external_id,
    user_role: mapUserRole(whoami?.role),
    source_plan_tier: mapPlanTier(whoami),
  };
}
